import React from 'react';
import useInView from '../../hooks/useInView';

/**
 * Reveals its children the first time they scroll into view.
 *
 * `variant` picks the motion — see the `[data-reveal]` rules in index.css:
 *   up | left | right | scale | fade | mask (an image wipe) | rule (a drawn line)
 */
export default function Reveal({
  as: Tag = 'div',
  variant = 'up',
  delay = 0,
  className = '',
  style,
  children,
  ...rest
}) {
  const [ref, inView] = useInView();

  return (
    <Tag
      ref={ref}
      data-reveal={variant}
      className={`${inView ? 'is-in' : ''} ${className}`.trim()}
      style={{ transitionDelay: delay ? `${delay}ms` : undefined, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Cascades a list in as one gesture: the container is what gets observed, so
 * the items keep their relative timing however far down the page they sit.
 * Each child is given the reveal attributes and its index, which `.stagger`
 * turns into a transition delay.
 */
export function RevealGroup({
  as: Tag = 'div',
  variant = 'up',
  stagger = 70,
  delay = 0,
  className = '',
  style,
  children,
  ...rest
}) {
  const [ref, inView] = useInView();

  return (
    <Tag
      ref={ref}
      className={`stagger ${className}`.trim()}
      style={{ '--stagger': `${stagger}ms`, '--delay': `${delay}ms`, ...style }}
      {...rest}
    >
      {React.Children.map(children, (child, i) =>
        React.isValidElement(child)
          ? React.cloneElement(child, {
              'data-reveal': variant,
              className: `${child.props.className || ''} ${inView ? 'is-in' : ''}`.trim(),
              style: { '--i': i, ...child.props.style },
            })
          : child
      )}
    </Tag>
  );
}
